"use client"

import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  Plus,
  Folder,
  Search,
  Loader,
  Edit,
  Share2,
  Trash2,
  X,
  MoreVertical,
  Eye,
  Check,
  FileText,
  Calendar,
  Clock,
  AlertCircle,
  ChevronDown,
  Filter,
  Grid3X3,
  List,
} from "lucide-react"
import { getAllAssigmentData, getAssignmentData } from "./api"
import { putAws } from "../putAws"
import { postAws } from "../postAws"

const FlashMessage = ({ message, type, onDismiss }) => {
  return (
    <div
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 p-4 rounded-lg shadow-xl max-w-md w-full flex items-center justify-between animate-fade-in ${
        type === "error" ? "bg-gradient-to-r from-red-500 to-red-600" : "bg-gradient-to-r from-green-500 to-green-600"
      }`}
    >
      <div className="flex items-center">
        {type === "error" ? (
          <AlertCircle className="text-white mr-3 flex-shrink-0" size={20} />
        ) : (
          <Check className="text-white mr-3 flex-shrink-0" size={20} />
        )}
        <span className="text-white font-medium">{message}</span>
      </div>
      <button
        onClick={onDismiss}
        className="text-white/80 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
      >
        <X size={18} />
      </button>
    </div>
  )
}

const AssignmentCard = ({
  assignment,
  handleEdit,
  handleResponses,
  handleDelete,
  handleShareToClass,
  handleShareWithStudents,
}) => {
  const [showOptions, setShowOptions] = useState(false)
  const [showStatus, setShowStatus] = useState(false)
  const optionsRef = useRef(null)
  const navigate = useNavigate()
  const organisationId = localStorage.getItem("organisationId")

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target)) {
        setShowOptions(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const handleCardClick = () => {
    // Card click does nothing as per original code
  }

  const handleOptionClick = (e, action) => {
    e.stopPropagation() // Prevent the card click event from firing
    action()
    setShowOptions(false)
  }

  const handleViewResponse = (e) => {
    e.stopPropagation() // Prevent the card click event from firing
    handleResponses(assignment._id)
  }

  // Format the due date
  const formatDueDate = (dateString) => {
    if (!dateString) return "No due date"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    })
  }

  // Get status color
  const getStatusColor = (status) => {
    return status === "creating" ? "from-amber-400 to-amber-500" : "from-emerald-400 to-emerald-500"
  }

  return (
    <div
      className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden flex flex-col relative cursor-pointer group max-w-xs"
      onClick={handleCardClick}
    >
      {/* Status Badge */}
      <div
        className={`absolute top-3 left-3 z-10 rounded-full px-2 py-1 text-xs font-medium text-white bg-gradient-to-r ${getStatusColor(assignment.assignmentStatus)}`}
        onMouseEnter={() => setShowStatus(true)}
        onMouseLeave={() => setShowStatus(false)}
      >
        {assignment.assignmentStatus === "creating" ? (
          <div className="flex items-center">
            <div className="flex space-x-1 mr-1">
              <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: "0s" }}></div>
              <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
              <div className="w-1 h-1 bg-white rounded-full animate-bounce" style={{ animationDelay: "0.4s" }}></div>
            </div>
            <span className={`${showStatus ? "inline-block" : "hidden"} ml-1`}>Creating</span>
          </div>
        ) : (
          <div className="flex items-center">
            <Check size={12} className="mr-1" />
            <span className={`${showStatus ? "inline-block" : "hidden"}`}>Created</span>
          </div>
        )}
      </div>

      {/* More Options Button */}
      <div className="absolute top-3 right-3 z-10" onClick={(e) => e.stopPropagation()} ref={optionsRef}>
        <button
          onClick={() => setShowOptions(!showOptions)}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none"
          aria-label="More options"
        >
          <MoreVertical size={18} className="text-white" />
        </button>
        {showOptions && (
          <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-10 border border-gray-100 py-1 animate-scale-in">
            <button
              onClick={(e) => handleOptionClick(e, () => handleEdit(assignment._id))}
              className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
            >
              <Edit size={16} className="mr-2 text-indigo-500" />
              Edit Assignment
            </button>
            <button
              onClick={(e) => handleOptionClick(e, () => handleDelete(assignment))}
              className="w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
            >
              <Trash2 size={16} className="mr-2 text-red-500" />
              Delete Assignment
            </button>
            <button
              onClick={(e) => handleOptionClick(e, () => handleShareToClass(assignment))}
              disabled={assignment.assignmentStatus !== "created"}
              className={`w-full px-4 py-2.5 text-sm flex items-center transition-colors ${
                assignment.assignmentStatus === "created"
                  ? "text-gray-700 hover:bg-gray-50"
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              <Share2 size={16} className="mr-2 text-blue-500" />
              Share to Other Classes
            </button>
          </div>
        )}
      </div>

      {/* Card Header with Gradient - Reduced size and moved title here */}
      <div className="bg-gradient-to-r from-indigo-500 to-blue-600 py-2.5 px-4 text-white">
        <h3 className="text-base font-semibold text-center text-white line-clamp-2">{assignment.assignmentTitle}</h3>
      </div>

      {/* Card Content */}
      <div className="p-4 flex-grow flex flex-col">
        <div className="text-center mb-3">
          <p className="text-xs text-gray-500">{assignment.assignment_topic || "No topic"}</p>
        </div>
        {/* Due Date */}
        <div className="flex items-center justify-center mb-3 text-sm text-gray-600">
          <Calendar size={14} className="mr-1.5 text-indigo-500" />
          <span>Due Date: {formatDueDate(assignment.assignmentDueDate)}</span>
        </div>
        {/* Divider */}
        <div className="border-t border-gray-100 my-2"></div>
        {/* Action Buttons */}
        {assignment.assignmentStatus !== "creating" && (
          <div className="mt-auto pt-2 grid grid-cols-2 gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleEdit(assignment._id)
              }}
              className={`flex items-center justify-center px-3 py-1.5 text-sm rounded-lg transition-colors duration-300 ${
                assignment.shareWithStudents
                  ? "text-blue-600 bg-blue-50 hover:bg-blue-100"
                  : "text-emerald-600 bg-emerald-50 hover:bg-emerald-100"
              }`}
            >
              {assignment.shareWithStudents ? (
                <>
                  <Eye size={16} className="mr-1.5" />
                  View
                </>
              ) : (
                <>
                  <Edit size={16} className="mr-1.5" />
                  Edit
                </>
              )}
            </button>
            {assignment.shareWithStudents ? (
              <button
                onClick={handleViewResponse}
                className="flex items-center justify-center px-3 py-1.5 text-sm rounded-lg text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors duration-300"
              >
                <FileText size={16} className="mr-1.5" />
                Responses
              </button>
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleShareWithStudents(assignment._id)
                }}
                className="flex items-center justify-center px-3 py-1.5 text-sm rounded-lg text-purple-600 bg-purple-50 hover:bg-purple-100 transition-colors duration-300"
                title="Share with students"
              >
                <Share2 size={16} className="mr-1.5" />
                Share
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default function CreateAssignment() {
  const [assignments, setAssignments] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [flashMessage, setFlashMessage] = useState(null)
  const [isDeletingAssignment, setIsDeletingAssignment] = useState(false)
  const [assignmentToDelete, setAssignmentToDelete] = useState(null)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedAssignment, setSelectedAssignment] = useState(null)
  const [availableClasses, setAvailableClasses] = useState([])
  const [selectedClass, setSelectedClass] = useState(null)
  const [isSharing, setIsSharing] = useState(false)
  const [activeFilter, setActiveFilter] = useState("all")
  const [showFilterDropdown, setShowFilterDropdown] = useState(false)
  const [viewMode, setViewMode] = useState("card")
  const organisationId = localStorage.getItem("organisationId")
  const [activeBookMenu, setActiveBookMenu] = useState(null)

  useEffect(() => {
    const fetchAssignments = async () => {
      setIsLoading(true)
      try {
        const classRoomId = localStorage.getItem("classRoomId")
        if (!classRoomId) {
          throw new Error("No classRoomId found in localStorage")
        }
        const response = await getAllAssigmentData(classRoomId)
        console.log(response, "response from server")
        if (Array.isArray(response)) {
          setAssignments(response)
        } else if (response.statusCode === 200 && Array.isArray(response.body)) {
          setAssignments(response.body)
        } else {
          throw new Error("Invalid response from API")
        }
      } catch (err) {
        console.error("Error fetching assignments:", err)
        setFlashMessage({ type: "error", message: err.message })
        setAssignments([])
      } finally {
        setIsLoading(false)
      }
    }
    fetchAssignments()
    fetchAvailableClasses()
  }, [])

  useEffect(() => {
    if (flashMessage) {
      const timer = setTimeout(() => {
        setFlashMessage(null)
      }, 3000)
      return () => clearTimeout(timer)
    }
  }, [flashMessage])

  const fetchAvailableClasses = () => {
    const teacherData = JSON.parse(localStorage.getItem("teacherData"))
    if (teacherData && teacherData.createdClasses) {
      const currentClassRoomId = localStorage.getItem("classRoomId")
      const otherClasses = teacherData.createdClasses.filter((cls) => cls.classRoomId !== currentClassRoomId)
      setAvailableClasses(otherClasses)
    }
  }

  const handleNewAssignment = () => {
    navigate("/assignments/description")
  }

  const handleEdit = (assignmentId) => {
    navigate(`/assignment/edit/${assignmentId}`)
  }

  const handleResponses = (assignmentId) => {
    navigate(`/assignment/responses/${assignmentId}`)
  }

  const handleDelete = (assignment) => {
    console.log("Delete called with assignment:", assignment)
    setAssignmentToDelete(assignment)
    setShowDeleteConfirmation(true)
  }

  const confirmDelete = async () => {
    if (assignmentToDelete && assignmentToDelete.confirmText === "permanently delete") {
      setIsDeletingAssignment(true)
      try {
        const response = await postAws("https://api-dev.aurait.ai/assignment", "POST", {
          request: "delete-Assignment",
          body: {
            assignmentId: assignmentToDelete._id,
            teacherId: localStorage.getItem("teacherId"),
          },
        })
        if (response.statusCode === 200) {
          setAssignments(assignments.filter((a) => a._id !== assignmentToDelete._id))
          setShowDeleteConfirmation(false)
          setAssignmentToDelete(null)
          setFlashMessage({ type: "success", message: "Assignment deleted successfully" })
        } else {
          throw new Error(response.body.message || "Failed to delete assignment")
        }
      } catch (err) {
        console.error("Error deleting assignment:", err)
        setFlashMessage({ type: "error", message: err.message })
      } finally {
        setIsDeletingAssignment(false)
      }
    }
  }

  const handleShareToClass = async (assignment) => {
    if (assignment.assignmentStatus === "created") {
      setSelectedAssignment(assignment)
      setShowShareModal(true)
    } else {
      setFlashMessage({ type: "error", message: "Only assignments with 'created' status can be shared." })
    }
  }

  const confirmShare = async () => {
    if (!selectedClass || !selectedAssignment) return
    setIsSharing(true)
    try {
      const response = await getAssignmentData(selectedAssignment._id)
      const assignmentData = response.body.data
      const { classCode, classRoomId } = selectedClass
      const assignmentDataToShare = {
        ...assignmentData,
        classCode,
        classRoomId,
        shareWithStudents: false,
        assignmentStatus: "created",
        maxMarks: assignmentData.assignmentQuestion.reduce((total, q) => total + (q.maxMarks || 0), 0).toString(),
        organisationId: organisationId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        __v: 0,
      }

      const shareResponse = await postAws("https://api-dev.aurait.ai/assignment", "POST", {
        body: assignmentDataToShare,
        request: "create-Assignment",
      })

      if (shareResponse.statusCode === 201) {
        setFlashMessage({
          type: "success",
          message: `Assignment shared to ${selectedClass.classValue}-${selectedClass.subject} successfully!`,
        })
        setShowShareModal(false)
      } else {
        throw new Error(shareResponse.body ? JSON.parse(shareResponse.body).message : "Failed to share assignment")
      }
    } catch (error) {
      console.error("Error sharing assignment:", error)
      setFlashMessage({ type: "error", message: "Failed to share assignment. Please try again." })
    } finally {
      setIsSharing(false)
    }
  }

  const handleShareWithStudents = async (assignmentId) => {
    const assignmentData = {
      shareWithStudents: true,
    }
    setIsLoading(true)
    try {
      const url = `https://api-dev.aurait.ai/assignment?id=${assignmentId}`
      const response = await putAws(url, assignmentData)
      if (response.statusCode === 200) {
        setAssignments(assignments.map((a) => (a._id === assignmentId ? { ...a, shareWithStudents: true } : a)))
        setFlashMessage({ type: "success", message: "Assignment shared with students successfully" })
      } else {
        throw new Error(response.body.message || "Failed to share assignment with students")
      }
    } catch (err) {
      console.error("Error sharing assignment with students:", err)
      setFlashMessage({ type: "error", message: err.message })
    }
    setIsLoading(false)
  }

  const getFilteredAssignments = () => {
    let filtered = assignments
    filtered = filtered.filter((assignment) =>
      (assignment.assignmentTitle || assignment.assignment_topic).toLowerCase().includes(searchTerm.toLowerCase()),
    )
    switch (activeFilter) {
      case "shared":
        return filtered.filter((a) => a.shareWithStudents)
      case "pending":
        return filtered.filter((a) => a.assignmentStatus === "creating")
      case "not-shared":
        return filtered.filter((a) => !a.shareWithStudents && a.assignmentStatus === "created")
      default:
        return filtered
    }
  }

  const getFilterLabel = () => {
    switch (activeFilter) {
      case "shared":
        return "Shared"
      case "pending":
        return "Pending"
      case "not-shared":
        return "Not Shared"
      default:
        return "All"
    }
  }

  const getStatusColor = (status) => {
    return status === "creating" ? "bg-amber-500" : "bg-emerald-500"
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-100 -mt-6">
      {flashMessage && (
        <FlashMessage message={flashMessage.message} type={flashMessage.type} onDismiss={() => setFlashMessage(null)} />
      )}

      <div className="container mx-auto px-2 md:px-4 py-0 max-w-7xl">
        {/* Header Section with Search, Filter, View Toggle, and Create Button */}
        <div className="bg-white rounded-xl md:rounded-2xl shadow-md overflow-hidden mt-6 md:mt-8">
          <div className="bg-gradient-to-r from-indigo-500 to-blue-600 px-3 md:px-4 py-3 md:py-4">
            {/* Mobile Layout */}
            <div className="block lg:hidden">
              <div className="flex items-center gap-2">
                {/* View Toggle - moved to left */}
                <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                  <button
                    onClick={() => setViewMode("card")}
                    className={`p-1.5 rounded-l-lg transition-colors ${
                      viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <Grid3X3 size={12} />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-1.5 rounded-r-lg transition-colors ${
                      viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                    }`}
                  >
                    <List size={12} />
                  </button>
                </div>
                {/* Search */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-3 py-1.5 pl-7 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-xs"
                  />
                  <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-white/70" size={12} />
                </div>
                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/20 backdrop-blur-sm text-white text-xs font-medium border border-white/30"
                  >
                    <Filter size={12} />
                    <ChevronDown
                      size={10}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-28 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All", color: "indigo" },
                        { key: "shared", label: "Shared", color: "green" },
                        { key: "not-shared", label: "Not Shared", color: "blue" },
                        { key: "pending", label: "Pending", color: "amber" },
                      ].map(({ key, label, color }) => (
                        <button
                          key={key}
                          onClick={() => {
                            setActiveFilter(key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-2 py-1.5 text-xs text-left flex items-center transition-colors hover:bg-gray-50 ${
                            activeFilter === key ? `bg-${color}-50 text-${color}-700` : "text-gray-700"
                          }`}
                        >
                          <span className={`w-1 h-1 rounded-full bg-${color}-500 mr-1.5`}></span>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Create Button */}
                <button
                  onClick={handleNewAssignment}
                  className="bg-white/20 backdrop-blur-sm text-white px-2 py-1.5 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-1 border border-white/30 text-xs font-medium"
                >
                  <Plus size={12} />
                  <span>Create</span>
                </button>
              </div>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:flex lg:items-center justify-between gap-4">
              {/* View Toggle - moved to left side */}
              <div className="flex items-center bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                <button
                  onClick={() => setViewMode("card")}
                  className={`flex items-center gap-1 p-2 rounded-l-lg transition-colors text-xs ${
                    viewMode === "card" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="Card View"
                >
                  <Grid3X3 size={14} />
                  <span>Card</span>
                </button>
                <button
                  onClick={() => setViewMode("list")}
                  className={`flex items-center gap-1 p-2 rounded-r-lg transition-colors text-xs ${
                    viewMode === "list" ? "bg-white/30 text-white" : "text-white/70 hover:text-white"
                  }`}
                  title="List View"
                >
                  <List size={14} />
                  <span>List</span>
                </button>
              </div>

              {/* Search, Filter, and Create - Right side */}
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search assignments"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-64 px-4 py-2 pl-10 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-white/50 transition-all duration-300 ease-in-out bg-white/10 backdrop-blur-sm text-white placeholder-white/70 text-sm"
                  />
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/70" size={14} />
                </div>
                {/* Filter */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium border border-white/30 hover:bg-white/30 transition-colors"
                  >
                    <Filter size={14} />
                    <span>{getFilterLabel()}</span>
                    <ChevronDown
                      size={14}
                      className={`transition-transform ${showFilterDropdown ? "rotate-180" : ""}`}
                    />
                  </button>
                  {showFilterDropdown && (
                    <div className="absolute right-0 mt-1 w-36 bg-white rounded-md shadow-lg z-10 border border-gray-200 py-1 animate-scale-in">
                      {[
                        { key: "all", label: "All", color: "indigo" },
                        { key: "shared", label: "Shared", color: "green" },
                        { key: "not-shared", label: "Not Shared", color: "blue" },
                        { key: "pending", label: "Pending", color: "amber" },
                      ].map(({ key, label, color }) => (
                        <button
                          key={key}
                          onClick={() => {
                            setActiveFilter(key)
                            setShowFilterDropdown(false)
                          }}
                          className={`w-full px-3 py-2 text-sm text-left flex items-center transition-colors hover:bg-gray-50 ${
                            activeFilter === key ? `bg-${color}-50 text-${color}-700` : "text-gray-700"
                          }`}
                        >
                          <span className={`w-1.5 h-1.5 rounded-full bg-${color}-500 mr-2`}></span>
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {/* Create Button */}
                <button
                  onClick={handleNewAssignment}
                  className="bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-lg hover:bg-white/30 transition-colors duration-300 flex items-center gap-2 border border-white/30 text-sm font-medium"
                >
                  <Plus size={16} />
                  <span>Create Assignment</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-3 md:p-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
                <p className="text-gray-600 font-medium">Loading assignments...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-100 text-indigo-600 mb-4">
                  <Folder className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Your library is empty</h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  Create your first assignment to start engaging with your students.
                </p>
                <button
                  onClick={handleNewAssignment}
                  className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-300 flex items-center gap-2 mx-auto"
                >
                  <Plus size={20} />
                  <span>Create Your First Assignment</span>
                </button>
              </div>
            ) : getFilteredAssignments().length === 0 ? (
              <div className="text-center py-16 bg-gray-50 rounded-xl border-2 border-dashed border-gray-200">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    activeFilter === "pending"
                      ? "bg-amber-100 text-amber-600"
                      : activeFilter === "shared"
                        ? "bg-green-100 text-green-600"
                        : activeFilter === "not-shared"
                          ? "bg-blue-100 text-blue-600"
                          : "bg-indigo-100 text-indigo-600"
                  }`}
                >
                  {activeFilter === "pending" ? (
                    <Clock className="w-8 h-8" />
                  ) : activeFilter === "shared" ? (
                    <Share2 className="w-8 h-8" />
                  ) : activeFilter === "not-shared" ? (
                    <Eye className="w-8 h-8" />
                  ) : (
                    <Folder className="w-8 h-8" />
                  )}
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {activeFilter === "pending"
                    ? "Woohoo! No pending assignments"
                    : activeFilter === "shared"
                      ? "Woohoo! No shared assignments"
                      : activeFilter === "not-shared"
                        ? "Woohoo! No not shared assignments"
                        : "No matching assignments found"}
                </h3>
                <p className="text-gray-500 max-w-md mx-auto">
                  {activeFilter === "all"
                    ? "Try adjusting your search term to find what you're looking for."
                    : "Try selecting a different filter to see other assignments."}
                </p>
              </div>
            ) : viewMode === "card" ? (
              // Card View
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {getFilteredAssignments().map((assignment) => (
                  <AssignmentCard
                    key={assignment._id}
                    assignment={assignment}
                    handleEdit={handleEdit}
                    handleResponses={handleResponses}
                    handleDelete={handleDelete}
                    handleShareToClass={handleShareToClass}
                    handleShareWithStudents={handleShareWithStudents}
                  />
                ))}
              </div>
            ) : (
              // List View - Table Format
              <div className="bg-white rounded-lg shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assignment Title
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Topic
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Due Date
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {getFilteredAssignments().map((assignment) => (
                        <tr key={assignment._id} className="hover:bg-gray-50 transition-colors duration-200">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                                <FileText className="w-4 h-4 text-white" />
                              </div>
                              <div className="text-sm font-medium text-gray-900 max-w-xs truncate">
                                {assignment.assignmentTitle}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                              {assignment.assignment_topic || "N/A"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <div className="flex items-center">
                              <Calendar size={14} className="mr-2 text-gray-400" />
                              {new Date(assignment.assignmentDueDate).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium text-white ${getStatusColor(
                                assignment.assignmentStatus,
                              )}`}
                            >
                              {assignment.assignmentStatus === "creating" ? "Pending" : "Created"}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={() => handleEdit(assignment._id)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                              >
                                <Edit size={14} className="mr-1" />
                                Edit
                              </button>
                              <button
                                onClick={() => handleResponses(assignment._id)}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                              >
                                <FileText size={14} className="mr-1" />
                                Responses
                              </button>
                              <div className="relative">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setActiveBookMenu(activeBookMenu === assignment._id ? null : assignment._id)
                                  }}
                                  className="inline-flex items-center p-1.5 border border-gray-300 rounded-md text-gray-400 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                                  aria-label="Assignment options"
                                >
                                  <MoreVertical size={16} />
                                </button>
                                {activeBookMenu === assignment._id && (
                                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl z-10 border border-gray-100 py-1 animate-scale-in">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleShareWithStudents(assignment._id)
                                        setActiveBookMenu(null)
                                      }}
                                      disabled={assignment.shareWithStudents}
                                      className={`w-full px-4 py-2.5 text-sm flex items-center transition-colors ${
                                        assignment.shareWithStudents
                                          ? "text-gray-400 cursor-not-allowed"
                                          : "text-gray-700 hover:bg-gray-50"
                                      }`}
                                    >
                                      <Share2 size={16} className="mr-2 text-purple-500" />
                                      Share with Students
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleShareToClass(assignment)
                                        setActiveBookMenu(null)
                                      }}
                                      disabled={assignment.assignmentStatus !== "created"}
                                      className={`w-full px-4 py-2.5 text-sm flex items-center transition-colors ${
                                        assignment.assignmentStatus === "created"
                                          ? "text-gray-700 hover:bg-gray-50"
                                          : "text-gray-400 cursor-not-allowed"
                                      }`}
                                    >
                                      <Share2 size={16} className="mr-2 text-blue-500" />
                                      Share to Other Classes
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        handleDelete(assignment)
                                        setActiveBookMenu(null)
                                      }}
                                      className="w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                                    >
                                      <Trash2 size={16} className="mr-2" />
                                      Delete Assignment
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-2xl animate-scale-in">
            <div className="bg-red-50 p-4 rounded-lg mb-6 flex items-start">
              <Trash2 className="text-red-500 mr-3 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <h2 className="text-lg font-bold text-red-700 mb-1">Delete Assignment</h2>
                <p className="text-red-600 text-sm">
                  This action cannot be undone. All assignment data will be permanently removed.
                </p>
              </div>
            </div>
            <p className="mb-2 text-gray-700 font-medium">Type "permanently delete" to confirm:</p>
            <input
              type="text"
              className="w-full p-3 border border-gray-300 rounded-lg mb-6 focus:ring-2 focus:ring-red-500 focus:border-red-500"
              placeholder="Type here"
              onChange={(e) =>
                setAssignmentToDelete({
                  ...assignmentToDelete,
                  confirmText: e.target.value,
                })
              }
            />
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false)
                  setAssignmentToDelete(null)
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={assignmentToDelete?.confirmText !== "permanently delete" || isDeletingAssignment}
                className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium disabled:bg-red-300 disabled:cursor-not-allowed"
              >
                {isDeletingAssignment ? (
                  <>
                    <Loader className="animate-spin mr-2" size={18} />
                    Deleting...
                  </>
                ) : (
                  "Delete Assignment"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share to Class Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
          <div className="bg-white p-6 rounded-xl w-full max-w-md shadow-2xl animate-scale-in">
            <div className="bg-blue-50 p-4 rounded-lg mb-6 flex items-start">
              <Share2 className="text-blue-500 mr-3 mt-0.5 flex-shrink-0" size={20} />
              <div>
                <h2 className="text-lg font-bold text-blue-700 mb-1">Share Assignment</h2>
                <p className="text-blue-600 text-sm">Share this assignment with another class you teach.</p>
              </div>
            </div>
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select destination class:</label>
              <div className="relative">
                <select
                  className="w-full p-3 border border-gray-300 rounded-lg appearance-none bg-white pr-10 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  value={selectedClass ? selectedClass.classRoomId : ""}
                  onChange={(e) => setSelectedClass(availableClasses.find((c) => c.classRoomId === e.target.value))}
                >
                  <option value="">Select a class</option>
                  {availableClasses.map((cls) => (
                    <option key={cls.classRoomId} value={cls.classRoomId}>
                      {cls.classValue} - {cls.subject}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"
                  size={18}
                />
              </div>
              {availableClasses.length === 0 && (
                <p className="mt-2 text-sm text-amber-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" />
                  You don't have any other classes to share with.
                </p>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowShareModal(false)}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-300 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={confirmShare}
                disabled={!selectedClass || isSharing}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center shadow-md font-medium disabled:bg-blue-300 disabled:cursor-not-allowed"
              >
                {isSharing ? (
                  <>
                    <Loader className="animate-spin mr-2" size={18} />
                    Sharing...
                  </>
                ) : (
                  "Share Assignment"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
